import { useCallback, useEffect, useRef, useState } from 'react';
import { Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import Button from 'components/base/Button';
import { useDispatch } from 'react-redux';
import { setToast } from 'components/Toasts/data-access/toast-reducer';
import {
  ATACDocument,
  ATACResultsResponse,
  getATACResults,
  getATACRunStatus,
  hasPendingRun,
  runATACAnalysis
} from 'data-access/atac.api';

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_MS = 5 * 60 * 1000;

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return Number.isNaN(d.getTime()) ? dateStr : d.toLocaleString();
};

const runStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
      return <span className="badge bg-success">✅ Completed</span>;
    case 'failed':
      return <span className="badge bg-danger">Failed</span>;
    default:
      return <span className="badge bg-warning text-dark">⏳ Pending</span>;
  }
};

interface AnalysisTabProps {
  projectId: string;
  documents: ATACDocument[];
  results: ATACResultsResponse | null;
  onRunCompleted?: () => void;
}

const AnalysisTab = ({ projectId, documents, results, onRunCompleted }: AnalysisTabProps) => {
  const dispatch = useDispatch();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [runHistory, setRunHistory] = useState(results?.runs ?? []);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartRef = useRef<number | null>(null);

  useEffect(() => {
    setRunHistory(results?.runs ?? []);
  }, [results]);

  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    pollStartRef.current = null;
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const pollRunStatus = useCallback(
    (runId: string) => {
      stopPolling();
      pollStartRef.current = Date.now();
      setActiveRunId(runId);
      setIsAnalyzing(true);

      pollRef.current = setInterval(async () => {
        if (pollStartRef.current && Date.now() - pollStartRef.current > MAX_POLL_MS) {
          stopPolling();
          setIsAnalyzing(false);
          setActiveRunId(null);
          dispatch(
            setToast({
              show: true,
              title: 'Analysis timeout',
              message: 'Taking longer than expected. Refresh to check.'
            })
          );
          return;
        }

        try {
          const statusRes = await getATACRunStatus(projectId, runId);
          if (statusRes.data.status === 'completed') {
            stopPolling();
            setIsAnalyzing(false);
            setActiveRunId(null);
            const updated = await getATACResults(projectId);
            setRunHistory(updated.data?.runs ?? []);
            onRunCompleted?.();
            dispatch(
              setToast({
                show: true,
                title: 'Analysis complete',
                message: 'Trust assessment finished successfully.'
              })
            );
          } else if (statusRes.data.status === 'failed') {
            stopPolling();
            setIsAnalyzing(false);
            setActiveRunId(null);
            dispatch(
              setToast({
                show: true,
                title: 'Analysis failed',
                message: statusRes.data.error || 'The trust assessment run failed.'
              })
            );
          }
        } catch {
          // keep polling until timeout
        }
      }, POLL_INTERVAL_MS);
    },
    [projectId, dispatch, onRunCompleted, stopPolling]
  );

  const handleRunAnalysis = async () => {
    if (documents.length === 0) return;
    setIsAnalyzing(true);
    try {
      const response = await runATACAnalysis(projectId);
      const runId = response.data.runId;
      if (!runId) throw new Error('No run ID returned');
      pollRunStatus(runId);
    } catch (err: any) {
      setIsAnalyzing(false);
      dispatch(
        setToast({
          show: true,
          title: 'Analysis failed',
          message: err?.response?.data?.message || err?.message || 'Could not start analysis.'
        })
      );
    }
  };

  const pendingExists = hasPendingRun(runHistory) || Boolean(activeRunId);
  const canRun = documents.length > 0 && !isAnalyzing && !pendingExists;

  const reversedRuns = [...runHistory].reverse();

  return (
    <div>
      <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-4">
        <div>
          <h5 className="fw-semibold mb-1 d-flex align-items-center gap-2">
            <FontAwesomeIcon icon={faShieldHalved} className="text-primary" />
            Trust Analysis
          </h5>
          <p className="text-muted small mb-0">
            Run a deterministic trust assessment against uploaded evidence and model registration.
          </p>
        </div>
        <Button
          variant="success"
          disabled={!canRun}
          onClick={handleRunAnalysis}
          style={{ backgroundColor: canRun ? '#25b003' : undefined, borderColor: canRun ? '#25b003' : undefined }}
        >
          {isAnalyzing ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Analyzing...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faPlay} className="me-2" />
              Run Analysis
            </>
          )}
        </Button>
      </div>

      <div className="rounded-3 border bg-white overflow-hidden">
        <Table responsive hover className="mb-0 align-middle">
          <thead className="table-light">
            <tr>
              <th>Run</th>
              <th>Started</th>
              <th>Status</th>
              <th>Completed</th>
            </tr>
          </thead>
          <tbody>
            {reversedRuns.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center text-muted py-4">
                  No analysis runs yet. Upload evidence and run analysis to get started.
                </td>
              </tr>
            ) : (
              reversedRuns.map((run, index) => (
                <tr key={run.runId}>
                  <td>Run #{reversedRuns.length - index}</td>
                  <td className="text-muted small">{formatDate(run.startedAt)}</td>
                  <td>{runStatusBadge(run.status)}</td>
                  <td className="text-muted small">{formatDate(run.completedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default AnalysisTab;
